import { memo, useEffect, useRef, useCallback } from 'react';
import { Header } from "@/components/common";
import { ChevronRight } from 'lucide-react';
import { Link } from "react-router-dom";

interface BreadcrumbsProps {
	title: string;
	subtitle?: string;
	breadcrumbText: string;
	backgroundImage: string;
	titleClassName?: string;
}

const Breadcrumbs = memo(({ 
	title, 
	subtitle, 
	breadcrumbText, 
	backgroundImage,
	titleClassName = "text-4xl md:text-6xl font-bold mb-5 mt-1"
}: BreadcrumbsProps) => {
	const parallaxRef = useRef<HTMLDivElement>(null);

	const handleScroll = useCallback(() => {
		if (parallaxRef.current) {
			const rate = window.scrollY * 0.5;
			parallaxRef.current.style.transform = `translate3d(0, ${rate}px, 0)`;
		}
	}, []);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, [handleScroll]);

	return (
		<section className="text-white relative w-full overflow-hidden">
			{/* Parallax Background Image */}
			<div
				ref={parallaxRef}
				className="absolute inset-0 w-full h-[120%] transition-transform duration-0"
				style={{
					backgroundImage: `url(${backgroundImage})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					willChange: 'transform',
					top: '-10%'
				}}
			/>

			{/* Overlay */}
			<div className="absolute inset-0 bg-[#000] opacity-50 pointer-events-none" />
			
			<div className="relative">
				<Header />
				<div className="py-16 md:py-40">
					<div className="container mx-auto px-6 text-center">
						{subtitle && (
							<span className="text-center text-branding lg:text-xl">{subtitle}</span>
						)}
						<h1 className={titleClassName}>{title}</h1>
						<div className="flex items-center justify-center gap-2 lg:text-[20px] text-gray-200">
							<Link to="/" className="hover:text-white transition-colors">
								Home
							</Link>
							<span>
								<ChevronRight className="w-[15px] h-[15px] text-textColor" strokeWidth={2.5} />
							</span>
							<span>{breadcrumbText}</span>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
});

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;